import type { Instance } from "@gadgetinc/mobx-quick-tree";
import validate from "validate.js";
import type { ShopifyBasePartnerAppConfig } from "../../connections/shopify/ShopifyBasePartnerAppConfig";
import type { ShopifyPartnerAppConfig } from "../../connections/shopify/ShopifyPartnerAppConfig";
import { ShopifyTomlConfig } from "../../connections/shopify/ShopifyTomlConfig";
import { RichString } from "../../RichString";
import { WorkspacePageType } from "../../WorkspacePageType";
import { createProblemFinder } from "../createProblemFinder";
import { ProblemSeverity } from "../ProblemSeverity";

validate.validators.tomlAgainstConnectionScopes = (value: ShopifyTomlConfig, options: Record<string, any>, _key: any, node: any) => {
  // Returns problem message if there exists scope(s) in the TOML that don't exist in the connection
  if (!value) return;

  const connectionConfig = node.shopifyConnectionConfig;

  if (connectionConfig) {
    if (!value.shouldShowProblems) return;

    const tomlScopes: string[] = Array.from(value.scopes?.values() ?? []);
    const connectionScopes = Array.from(connectionConfig.requestedScopes.values());

    const scopesDiff = tomlScopes.filter((scope) => !connectionScopes.includes(scope));

    if (scopesDiff.length > 0) {
      return `Scope mismatch. ${RichString.text(JSON.stringify(scopesDiff))} in ${RichString.text(
        value.linkedTomlFile
      )} TOML do not exist in app settings.`;
    }
  }
};

validate.validators.hasConnectedTomlConfig = (value: string, options: Record<string, any>, _key: any, node: ShopifyPartnerAppConfig) => {
  // Returns problem message if the app config does not have a linkedTomlFile
  if (!value) return;

  const tomlConfig = node.tomlConfig;

  if (!tomlConfig) return;
  const appHandle = node.appHandle;

  if (value !== tomlConfig?.clientId) {
    return `Shopify app configuration with ${appHandle ? "handle" : "client ID"} ${RichString.text(
      appHandle ?? value
    )} does not have a TOML file linked by client ID.`;
  }
};

/*
 * NOTE: These problems are also mirrored in
 * ShopifyConnectionFormProblems, for local form state, any updates
 * here should also be applied there
 */
export const ShopifyBasePartnerAppConfigProblems = createProblemFinder<Instance<ReturnType<typeof ShopifyBasePartnerAppConfig>>>(
  {
    appURL: {
      url: {
        message: (_value, _attribute, _options, node) =>
          `Shopify app configuration with api key ${RichString.text(node.apiKey)} has an invalid app URL`,
        inContextMessage: "App URL must be a valid URL starting with https://",
        schemes: ["https"],
        allowLocal: true,
      },
    },
  },
  {
    name: "Shopify Connected App",
    tabType: WorkspacePageType.ShopifyConnectionsIndex,
    fullMessages: false,
  }
);

export const ShopifyPartnerAppConfigProblems = createProblemFinder<Instance<typeof ShopifyPartnerAppConfig>>(
  {
    appURL: {
      url: {
        message: (_value, _attribute, _options, node) =>
          `Shopify app configuration with api key ${RichString.text(node.apiKey)} has an invalid app URL`,
        inContextMessage: "App URL must be a valid URL starting with https://",
        schemes: ["https"],
        allowLocal: true,
      },
    },
    appURLDomain: {
      equality: {
        attribute: "appPrimaryDomain",
        message: (_value, _attribute, _options, node) => {
          return `App URL for ${RichString.text(node.appHandle ?? node.apiKey)} does not match primary domain: ${RichString.text(
            node.appPrimaryDomain
          )}`;
        },
        severity: ProblemSeverity.Warning,
      },
    },
    tomlConfig: {
      tomlAgainstConnectionScopes: {
        message: "TOML scope mismatch",
        inContextMessage: "^TOML scopes must match connection scopes",
        severity: ProblemSeverity.Warning,
      },
    },
    apiKey: {
      hasConnectedTomlConfig: {
        message: "No connected TOML file found",
        inContextMessage: "^No connected TOML file found",
        severity: ProblemSeverity.Warning,
      },
    },
  },
  {
    name: "Shopify Connected App",
    tabType: WorkspacePageType.ShopifyConnectionsIndex,
    fullMessages: false,
  }
);
